<template>
  <section class="co-surface co-period">
    <div class="co-period__bar">
      <div>
        <span class="co-period__label">{{ periodTriggerLabel }}</span>
        <p class="co-period__range">
          {{ periodRangeText }}
        </p>
        <p
          v-if="compareText"
          class="co-period__compare"
        >
          {{ compareText }}
        </p>
      </div>
      <div class="co-period__controls">
        <b-button
          size="sm"
          :variant="periodPreset === 'today' ? 'primary' : 'outline-primary'"
          class="co-period__btn"
          :disabled="loading"
          @click="$emit('period-preset', 'today')"
        >
          Hoje
        </b-button>
        <b-button
          size="sm"
          :variant="periodPreset === '7' ? 'primary' : 'outline-primary'"
          class="co-period__btn"
          :disabled="loading"
          @click="$emit('period-preset', '7')"
        >
          7 dias
        </b-button>
        <b-button
          size="sm"
          :variant="periodPreset === '30' ? 'primary' : 'outline-primary'"
          class="co-period__btn"
          :disabled="loading"
          @click="$emit('period-preset', '30')"
        >
          30 dias
        </b-button>
        <b-button
          size="sm"
          :variant="periodPreset === 'custom' ? 'primary' : 'outline-primary'"
          class="co-period__btn"
          :disabled="loading"
          @click="$emit('period-preset', 'custom')"
        >
          Personalizado
        </b-button>
      </div>
    </div>

    <div
      v-if="periodPreset === 'custom'"
      class="co-period__custom mb-3"
    >
      <b-form-datepicker
        v-model="localInicio"
        locale="pt-BR"
        size="sm"
        class="mr-2 d-inline-block"
        style="max-width: 200px"
        :disabled="loading"
        :date-format-options="{ day: 'numeric', month: 'short', year: 'numeric' }"
      />
      <b-form-datepicker
        v-model="localFim"
        locale="pt-BR"
        size="sm"
        class="d-inline-block"
        style="max-width: 200px"
        :disabled="loading"
        :date-format-options="{ day: 'numeric', month: 'short', year: 'numeric' }"
      />
      <b-button
        variant="primary"
        size="sm"
        class="ml-2"
        :disabled="loading"
        @click="$emit('apply-custom', { inicio: localInicio, fim: localFim })"
      >
        Aplicar
      </b-button>
    </div>

    <div
      v-if="loading"
      class="co-period__loading-banner"
      role="status"
      aria-live="polite"
    >
      <b-spinner
        small
        class="co-period__loading-spinner"
      />
      <span>Carregando dados do período…</span>
    </div>

    <div
      class="co-period__metrics-wrap"
      :class="{ 'co-period__metrics-wrap--loading': loading }"
    >
      <h2 class="co-section-title co-period__title">
        Desempenho do período
      </h2>

      <div class="co-metric-grid co-metric-grid--period">
        <div
          v-for="card in cards"
          :key="card.key"
          class="co-metric-card co-metric-card--period"
          :class="{ 'co-metric-card--clickable': card.clickable }"
          @click="card.clickable ? toggleCard(card.key) : null"
        >
          <div class="co-metric-card__header">
            <span class="co-metric-card__value">{{ card.value }}</span>
            <feather-icon
              v-if="card.clickable"
              :icon="activeCardKey === card.key ? 'ChevronUpIcon' : 'ChevronDownIcon'"
              size="16"
              class="co-metric-card__toggle-icon"
            />
          </div>
          <span
            v-if="card.detail"
            class="co-metric-card__detail"
          >{{ card.detail }}</span>
          <span class="co-metric-card__label">{{ card.label }}</span>
          <span
            v-if="card.delta != null && card.delta !== ''"
            class="co-delta"
            :class="card.deltaPositive ? 'co-delta--up' : 'co-delta--down'"
          >
            {{ card.delta }}
          </span>
          <div
            v-if="card.clickable && activeCardKey === card.key && card.details && card.details.length"
            class="co-metric-card__details-panel"
            @click.stop
          >
            <div
              v-for="item in card.details"
              :key="item.label"
              class="co-detail-item"
            >
              <span class="co-detail-item__label">{{ item.label }}</span>
              <span class="co-detail-item__value">{{ item.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="co-narrative">
        <p>{{ narrativePeriod }}</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CentralPeriodSection',
  props: {
    periodPreset: {
      type: String,
      default: '7',
    },
    periodTriggerLabel: {
      type: String,
      default: '',
    },
    periodRangeText: {
      type: String,
      default: '',
    },
    compareText: {
      type: String,
      default: '',
    },
    cards: {
      type: Array,
      default: () => [],
    },
    narrativePeriod: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    periodoInicio: {
      type: [Date, String],
      default: null,
    },
    periodoFim: {
      type: [Date, String],
      default: null,
    },
  },
  data() {
    return {
      localInicio: this.periodoInicio,
      localFim: this.periodoFim,
      activeCardKey: null,
    }
  },
  watch: {
    periodoInicio(v) {
      this.localInicio = v
    },
    periodoFim(v) {
      this.localFim = v
    },
  },
  methods: {
    toggleCard(key) {
      this.activeCardKey = this.activeCardKey === key ? null : key
    },
  },
}
</script>

<style scoped>
.co-surface {
  background: #faf8fc;
  border-radius: 24px;
  padding: 1.75rem 1.75rem 1.5rem;
  border: 1px solid rgba(106, 27, 154, 0.08);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
}

.co-section-title {
  margin: 0 0 0.35rem;
  font-size: 1.35rem;
  font-weight: 800;
  color: #4a148c;
  letter-spacing: -0.02em;
}

.co-period__title {
  margin-top: 0.5rem;
  margin-bottom: 1.25rem;
}

.co-period__bar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 1.25rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid rgba(106, 27, 154, 0.1);
}

.co-period__label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9e9e9e;
  font-weight: 700;
}

.co-period__range {
  margin: 0.25rem 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #424242;
}

.co-period__compare {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: #757575;
}

.co-period__controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.co-period__btn {
  border-radius: 999px !important;
  font-weight: 600;
}

.co-period__custom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.co-period__loading-banner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 1rem;
  margin-bottom: 1rem;
  border-radius: 14px;
  background: rgba(106, 27, 154, 0.09);
  border: 1px solid rgba(106, 27, 154, 0.14);
  font-size: 0.9rem;
  font-weight: 600;
  color: #5e35b1;
}

.co-period__loading-spinner {
  color: #6a1b9a !important;
}

.co-period__metrics-wrap--loading {
  opacity: 0.42;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.co-metric-grid--period {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

@media (max-width: 1200px) {
  .co-metric-grid--period {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .co-metric-grid--period {
    grid-template-columns: 1fr;
  }
}

.co-metric-card {
  background: #fff;
  border-radius: 16px;
  padding: 1.15rem 1rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
}

.co-metric-card--clickable {
  cursor: pointer;
  transition: box-shadow 0.18s ease, border-color 0.18s ease;
}

.co-metric-card--clickable:hover {
  box-shadow: 0 4px 18px rgba(106, 27, 154, 0.12);
  border-color: rgba(106, 27, 154, 0.18);
}

.co-metric-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.4rem;
}

.co-metric-card__toggle-icon {
  color: #9e9e9e;
  flex-shrink: 0;
  margin-top: 0.3rem;
}

.co-metric-card__details-panel {
  margin-top: 0.55rem;
  padding-top: 0.6rem;
  border-top: 1px solid rgba(106, 27, 154, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.co-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.co-detail-item__label {
  font-size: 0.78rem;
  color: #757575;
  line-height: 1.35;
}

.co-detail-item__value {
  font-size: 0.88rem;
  font-weight: 700;
  color: #4a148c;
  white-space: nowrap;
}

.co-metric-card__value {
  font-size: 1.65rem;
  font-weight: 800;
  color: #4a148c;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.co-metric-card__detail {
  font-size: 1.05rem;
  font-weight: 700;
  color: #7e57c2;
  line-height: 1.2;
  margin-top: -0.1rem;
}

.co-metric-card__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #757575;
  line-height: 1.35;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.co-delta {
  font-size: 0.8rem;
  font-weight: 700;
  margin-top: 0.25rem;
}

.co-delta--down {
  color: #26a69a;
}

.co-delta--up {
  color: #ef5350;
}

.co-narrative {
  padding: 1.1rem 1.25rem;
  border-radius: 16px;
  background: linear-gradient(90deg, rgba(106, 27, 154, 0.06) 0%, rgba(255, 111, 97, 0.06) 100%);
  border: 1px solid rgba(106, 27, 154, 0.1);
}

.co-narrative p {
  margin: 0;
  font-size: 1rem;
  line-height: 1.55;
  color: #424242;
  font-weight: 500;
}
</style>
